import type { ReactElement } from "react";
// Explicit React import: tsx --test compiles JSX with the classic runtime.
import React from "react";
import type { FillTone, GoalBarModel } from "../../lib/roster-lanes";

/*
 * Zoned goal bar: muted track, mint good zone, amber watch band, a fill in the
 * chip's tone, a 2px threshold, and a dotted fleet-median marker. Under the
 * always theme (D5) the track is 6px with a 4px value stroke, olive (the
 * theme's metric tones) and red only below, a muted olive target band and no
 * watch band. Purely
 * presentational and roster-agnostic; the cell text carries the value, so the
 * bar is aria-hidden. Relative imports only so it loads under tsx --test.
 */

const TONE_CLASSES: Record<FillTone, string> = {
  above: "bg-success brand:bg-metric-meets",
  meets: "bg-metric-meets",
  watch: "bg-metric-watch",
  below: "bg-metric-below",
};

function percent(fraction: number): string {
  return `${(fraction * 100).toFixed(2)}%`;
}

function zoneStyle(zone: [number, number]): { left: string; width: string } {
  const [from, to] = zone;

  return { left: percent(from), width: percent(Math.max(0, to - from)) };
}

export function GoalBar({ model }: { model: GoalBarModel }): ReactElement {
  return (
    <div
      aria-hidden="true"
      data-goal-bar=""
      className="relative h-2.5 w-full overflow-hidden rounded-full bg-secondary brand:h-1.5"
    >
      {model.goodZone && (
        <div
          data-goal-zone="good"
          className="absolute inset-y-0 bg-success-soft brand:bg-primary/16"
          style={zoneStyle(model.goodZone)}
        />
      )}
      {model.watchBand && (
        <div
          data-goal-zone="watch"
          className="absolute inset-y-0 bg-warning-soft brand:bg-transparent"
          style={zoneStyle(model.watchBand)}
        />
      )}
      {model.fillTone !== null && model.fillFraction !== null && (
        <div
          data-goal-fill={model.fillTone}
          className={[
            "absolute left-0 top-[3px] h-1 rounded-full brand:top-px",
            TONE_CLASSES[model.fillTone],
          ].join(" ")}
          style={{ width: percent(model.fillFraction) }}
        />
      )}
      {model.threshold !== null && (
        <div
          data-goal-threshold=""
          className="absolute inset-y-0 w-0.5 bg-foreground"
          style={{ left: percent(model.threshold) }}
        />
      )}
      {model.fleetMedian !== null && (
        <div
          data-goal-fleet-median=""
          className="absolute inset-y-0 w-0 border-l-2 border-dotted border-sand-400"
          style={{ left: percent(model.fleetMedian) }}
        />
      )}
    </div>
  );
}
